import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { type OnboardingStep, useOnboardingTour } from '@rio-cloud/rio-uikit/useOnboarding';
import { groupBy } from 'es-toolkit/compat';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../../api/redux/api.redux';
import { ApiCalls } from '../../api/redux/types';
import { fetchDiscountsThunk } from '../../common/discount/discounts.thunk';
import LoadingErrorState from '../../common/error/LoadingErrorState';
import { loadFromLocalStorage } from '../../common/onboarding/handleLocalStorage';
import { getShowOnboardingTip, onboardingActions } from '../../common/onboarding/redux/oboarding.redux';
import { Tips } from '../../common/onboarding/types';
import { LimitedPermissionsInfoBanner } from '../../common/permissions/LimitedPermissionsInfoBanner';
import { RenewDialogContainer } from '../../common/termedLevel/dialog/RenewDialog';
import { getContracts, termedLevelRenewalActions } from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import { fetchContractsThunk } from '../../common/termedLevel/termedLevel.thunk';
import type { TermedLevel } from '../../common/termedLevel/types';
import { CONTRACT_CHANGES_BOTTOM_SHEET_STORAGE_KEY, ContractChangesBottomSheet } from './ContractChangesBottomSheet';
import { isContractChangesBottomSheetEnabled } from './ContractChangesBottomSheetFeatureToggle';
import { ContractPanel } from './ContractPanel';
import { ContractStatsWidgetsContainer } from './ContractStatsWidgetsContainer';
import { ContractTableToolBarContainer } from './ContractTableToolBarContainer';
import { useContractDeepLinkingHook } from './contractDeepLinkingHook';
import { contractActions } from './redux/contract.redux';
import { getFilteredContractsByAssets } from './redux/contractSearchService';

interface Props {
    contracts: TermedLevel[];
    fetchContractsAndDiscounts: () => void;
    resetContractsState: () => void;
    hasError: boolean;
    isLoading: boolean;
    tableMessage?: string;
    isEmpty: boolean;
    showContractsPageTour: boolean;
    hideContractsPageTour: () => void;
}

const ContractsPage = (props: Props) => {
    const {
        contracts,
        fetchContractsAndDiscounts,
        hasError,
        isLoading,
        tableMessage,
        resetContractsState,
        isEmpty,
        showContractsPageTour,
        hideContractsPageTour,
    } = props;
    const intl = useIntl();
    const [showContractChangesBottomSheet, setShowContractChangesBottomSheet] = useState(
        () => isContractChangesBottomSheetEnabled() && !loadFromLocalStorage(CONTRACT_CHANGES_BOTTOM_SHEET_STORAGE_KEY)
    );

    useContractDeepLinkingHook();

    const onboardingSteps = useMemo<OnboardingStep[]>(
        () => [
            {
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.overview.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.overview.description' }),
                },
            },
            {
                element: '[data-onboarding-contracts-walkthrough-step="contract-view-stats-widget"]',
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.stats.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.stats.description' }),
                    side: 'bottom',
                    align: 'center',
                },
            },
            {
                element: '[data-onboarding-contracts-walkthrough-step="contract-view-table-toolbar"]',
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.toolbar.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.toolbar.description' }),
                    side: 'bottom',
                    align: 'start',
                },
            },
            {
                element: '[data-onboarding-contracts-walkthrough-step="contract-view-table"]',
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.table.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.table.description' }),
                    side: 'top',
                    align: 'start',
                },
            },
            {
                element: '[data-onboarding-contracts-walkthrough-step="contract-view-renew-single-button"]',
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.renewSingle.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.renewSingle.description' }),
                    side: 'left',
                    align: 'center',
                },
            },
            {
                element: '[data-onboarding-contracts-walkthrough-step="contract-view-renew-bulk-button"]',
                popover: {
                    title: intl.formatMessage({ id: 'marketplace.contracts.onboarding.renewBulk.title' }),
                    description: intl.formatMessage({ id: 'marketplace.contracts.onboarding.renewBulk.description' }),
                    side: 'top',
                    align: 'end',
                },
            },
        ],
        [intl]
    );

    const onboardingTour = useOnboardingTour({
        steps: onboardingSteps,
        prevBtnText: intl.formatMessage({ id: 'intl-msg:navigations.previous' }),
        nextBtnText: intl.formatMessage({ id: 'intl-msg:navigations.next' }),
        doneBtnText: intl.formatMessage({ id: 'marketplace.contracts.onboarding.done' }),
        showProgress: true,
        allowClose: true,
        disableActiveInteraction: true,
        onPopoverRender: (popover, state) => {
            if (state.activeIndex !== onboardingSteps.length - 1) {
                return;
            }

            // For analytics purposes: this allows us to track how many customers followed the Contracts onboarding tour through to completion.
            popover.nextButton.setAttribute('data-track-ga-event-trigger', 'click');
            popover.nextButton.setAttribute('data-track-ga-event-category', 'marketplace');
            popover.nextButton.setAttribute('data-track-ga-event-action', 'contractsOnboardingCompleted_click');
            popover.nextButton.setAttribute(
                'data-track-ga-event-label',
                'button::marketplace.contracts.onboarding.completed'
            );
        },
        onDestroyed: hideContractsPageTour,
    });

    useEffect(() => {
        fetchContractsAndDiscounts();
        return () => resetContractsState();
    }, []);

    useEffect(() => {
        if (
            !showContractChangesBottomSheet &&
            !isLoading &&
            !hasError &&
            !isEmpty &&
            showContractsPageTour &&
            !onboardingTour.isActive()
        ) {
            onboardingTour.run();
        }
    }, [showContractChangesBottomSheet, isLoading, hasError, isEmpty, showContractsPageTour]);

    let content: ReactNode;
    if (isLoading) {
        content = <Spinner text={<FormattedMessage id={'marketplace.loading'} />} isInverse={false} />;
    } else if (hasError) {
        content = <LoadingErrorState />;
    } else if (isEmpty) {
        content = (
            <EmptyState
                headline={<FormattedMessage id={'marketplace.contracts.noContractsPresent'} />}
                outerClassName='margin-top-15pct'
            />
        );
    } else {
        const groupedContracts = groupBy(contracts, 'productId');
        content = (
            <>
                <ContractStatsWidgetsContainer />
                <LimitedPermissionsInfoBanner />
                <ContractTableToolBarContainer />
                {Object.keys(groupedContracts).map(productId => (
                    <ContractPanel key={productId} contracts={groupedContracts[productId]} open={true} />
                ))}
                <div className='text-center'>{tableMessage && <FormattedMessage id={tableMessage} />}</div>
                <RenewDialogContainer titleId={'intl-msg:common-message.contracts.renewal'} />
            </>
        );
    }

    return (
        <>
            {showContractChangesBottomSheet && (
                <ContractChangesBottomSheet onDismiss={() => setShowContractChangesBottomSheet(false)} />
            )}
            {content}
        </>
    );
};

const mapDispatchToProps = (dispatch: RootDispatch) => ({
    fetchContractsAndDiscounts: () => {
        dispatch(fetchContractsThunk());
        dispatch(fetchDiscountsThunk());
    },
    resetContractsState: () => {
        dispatch(termedLevelRenewalActions.clearUpdateState());
        dispatch(contractActions.setSelectedContracts([]));
    },
    hideContractsPageTour: () => {
        dispatch(onboardingActions.setShowOnboardingTip({ type: Tips.CONTRACTS_PAGE_TOUR, value: false }));
    },
});

const mapStateToProps = (state: RootState) => {
    const { displayRows, message } = getFilteredContractsByAssets(state);
    return {
        isEmpty: getContracts(state).length === 0,
        contracts: displayRows,
        isLoading: getIsLoading(state, ApiCalls.TERMED_LEVEL_GET),
        hasError: getHasError(state, ApiCalls.TERMED_LEVEL_GET),
        tableMessage: message,
        showContractsPageTour: getShowOnboardingTip(state, Tips.CONTRACTS_PAGE_TOUR),
    };
};

export const ContractPageContainer = connect(mapStateToProps, mapDispatchToProps)(ContractsPage);
